import React from 'react';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TimezoneSelectorDropdown from './TimezoneSelectorDropdown';

/**
 * Collapsible sidebar group with nested items.
 * @param {{
 *   groupId: string,
 *   label: string,
 *   icon: React.ReactNode,
 *   open: boolean,
 *   onToggle: () => void,
 *   sectionActive: boolean,
 *   activeTab: string,
 *   children: Array<{ id: string, text: string, icon: React.ReactNode, disabled?: boolean, secondary?: string }>,
 *   onSelect: (id: string) => void,
 * }} props
 */
export default function NestedNavSection({
  groupId,
  label,
  icon,
  open,
  onToggle,
  sectionActive,
  activeTab,
  children,
  onSelect,
}) {
  if (!children.length) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <>
        <ListItem disablePadding>
          <ListItemButton
            selected={sectionActive}
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={`nav-group-${groupId}`}
          >
            <ListItemIcon sx={{ color: sectionActive ? 'primary.main' : 'inherit' }}>
              {icon}
            </ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit id={`nav-group-${groupId}`}>
          <List component="div" disablePadding dense>
            {children.map((child) => (
              <ListItem key={child.id} disablePadding>
                <ListItemButton
                  selected={activeTab === child.id}
                  disabled={child.disabled}
                  onClick={() => {
                    if (!child.disabled) onSelect(child.id);
                  }}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: activeTab === child.id ? 'primary.main' : 'inherit',
                    }}
                  >
                    {child.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={child.text}
                    secondary={child.secondary}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </>
      {groupId === 'settings' && (
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            mt: 0.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <TimezoneSelectorDropdown />
        </Box>
      )}
    </Box>
  );
}
